import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import {
  fetchOpenings,
  formatPay,
  EMPLOYMENT_LABELS,
  OpeningsResponse,
  RoleSummary,
} from '../services/careers';
import './Careers.css';

/**
 * The public job board — and, deliberately, a brand page.
 *
 * Hiring is branding: for most people who land here this is the first or second
 * thing they ever read about Lomi, and a lot of them are students, parents and
 * teachers who will never apply. So this page leads with what we are and what
 * we're honest about, and the roles follow. The salary is on every card on
 * purpose.
 */

const HOW_WE_HIRE = [
  {
    n: '01',
    title: 'You apply',
    body: 'Ten minutes. No CV required for most roles — we ask you questions instead, because what you say beats how your CV is formatted.',
  },
  {
    n: '02',
    title: 'A paid work sample',
    body: "Real work, and we pay you for it whether or not we hire you. Nobody at Lomi works for free, including candidates.",
  },
  {
    n: '03',
    title: 'A video call',
    body: '30–45 minutes with the founder. We talk about the work sample and about you. You get to ask us anything, including about the money.',
  },
  {
    n: '04',
    title: 'We meet in person',
    body: 'In Addis. Coffee, an hour, and a decision within a week of that — not a month of silence.',
  },
];

const RoleCard: React.FC<{ role: RoleSummary; onOpen: () => void }> = ({ role, onOpen }) => {
  const pay = formatPay(role);
  return (
    <button className="c-role" onClick={onOpen} aria-label={`Open the ${role.title} role`}>
      <div className="c-role-top">
        <h3 className="c-role-title">{role.title}</h3>
        {role.openings > 1 && <span className="c-role-count">{role.openings} openings</span>}
      </div>
      {role.summary && <p className="c-role-summary">{role.summary}</p>}
      <div className="c-role-meta">
        <span className="c-chip c-chip-pay">{pay || 'Compensation on the role page'}</span>
        <span className="c-chip">{EMPLOYMENT_LABELS[role.employment_type] || role.employment_type}</span>
        {role.location && <span className="c-chip c-chip-quiet">{role.location}</span>}
      </div>
      <span className="c-role-go">Read the full role →</span>
    </button>
  );
};

const Careers: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<OpeningsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Jobs at Lomi — build Ethiopian education';
    fetchOpenings().then(setData).catch((e) => setError(e.message));
  }, []);

  return (
    <div className="c-page">
      <header className="c-header">
        <button className="c-back" onClick={() => navigate('/')}>← Lomi</button>
        <div className="c-brand" onClick={() => navigate('/')}>
          <Logo height={26} withAmharic />
        </div>
      </header>

      <section className="c-hero">
        <p className="c-eyebrow">ሎሚ · We're hiring</p>
        <h1>
          We're building Ethiopia's study companion —<br />
          <span className="c-hero-accent">and we're building it with Ethiopians.</span>
        </h1>
        <p className="c-hero-sub">
          Lomi helps students prepare for the ESSLCE and the UAT. Twenty-six thousand of them
          have signed up. One person has been running it. That stops now.
        </p>
        {data && (
          <div className="c-hero-stats">
            <div><strong>{data.total_openings}</strong><span>openings</span></div>
            <div><strong>{data.total_roles}</strong><span>roles</span></div>
            <div><strong>26,000+</strong><span>students</span></div>
          </div>
        )}
      </section>

      <section className="c-honest">
        <h2>Three things we'll say up front</h2>
        <div className="c-honest-grid">
          <div className="c-honest-card">
            <span className="c-honest-num">1</span>
            <h3>The salary is on every job post</h3>
            <p>
              You should not have to guess, or negotiate blind, or find out at the end. We pay at
              or above the top of the local market for the role and we print the number.
            </p>
          </div>
          <div className="c-honest-card">
            <span className="c-honest-num">2</span>
            <h3>We pay for work samples</h3>
            <p>
              Every role has a short piece of real work in the process, and everyone who does it
              gets paid — whether we hire you or not.
            </p>
          </div>
          <div className="c-honest-card">
            <span className="c-honest-num">3</span>
            <h3>You'll get an answer</h3>
            <p>
              Yes or no, from a person, within five days of each stage. Being ignored after
              applying is the normal experience in this market. Not here.
            </p>
          </div>
        </div>
      </section>

      <section className="c-roles">
        <h2 className="c-section-title">Open roles</h2>
        {error && <p className="c-error">{error}</p>}
        {!data && !error && <p className="c-loading">Loading roles…</p>}
        {data?.teams.map((team) => (
          <div key={team.team} className="c-team">
            <h3 className="c-team-label">{team.label}</h3>
            <div className="c-role-grid">
              {team.roles.map((role) => (
                <RoleCard
                  key={role.slug}
                  role={role}
                  onOpen={() => navigate(`/careers/${role.slug}`)}
                />
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className="c-process">
        <h2 className="c-section-title">How we hire</h2>
        <div className="c-steps">
          {HOW_WE_HIRE.map((s) => (
            <div className="c-step" key={s.n}>
              <span className="c-step-n">{s.n}</span>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
        <p className="c-process-note">
          Start to finish, about three weeks. Everyone who applies gets free full access to Lomi,
          for themselves and two family members, whatever the outcome.
        </p>
      </section>

      <footer className="c-footer">
        <h2>Not the right role for you?</h2>
        <p>
          Send this page to a teacher. Almost everything on it is a job for someone who has stood
          in front of a grade-12 class — and they rarely hear about work like this.
        </p>
        <button className="c-footer-cta" onClick={() => navigate('/')}>
          See what Lomi actually does →
        </button>
      </footer>
    </div>
  );
};

export default Careers;
